import {
  CreateConciergeSupportIssueMutation,
  CreateConciergeSupportIssueMutationVariables
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import { useTrackEvent } from "@outschool/ui-analytics";
import { gql, useMutation } from "@outschool/ui-apollo";
import React, { useState } from "react";

import { SelectedLearner, useChatContext } from "../chatProvider";
import { ChatInputField } from "../components/ChatInputField";
import { LearnerSelection } from "../components/LearnerSelection";

const createConciergeSupportIssueMutation = gql`
  mutation CreateConciergeSupportIssue($userMessage: String!) {
    createConciergeSupportIssue(userMessage: $userMessage)
  }
`;

/**
 * example:
 * This is a class recommendation request.
 * --- Learner Info ---
 * Name: John
 * Age: 10
 * Pronoun: He/Him
 * schooling_approach: "homeschool"
 * Availability: Monday 10:00 AM - 11:00 AM
 * --- Parent Message ---
 * I need help with math homework.
 */
function buildUserMessage(
  inputValue: string,
  selectedLearner: SelectedLearner | undefined
) {
  let contextPrefix = "This is a class recommendation request.\n";
  let userMessage = inputValue;
  if (selectedLearner) {
    contextPrefix += `--- Learner Info ---\nName: ${selectedLearner.name}\n`;
    if (selectedLearner.age) {
      contextPrefix += `Age: ${selectedLearner.age}\n`;
    }
    if (selectedLearner.pronoun) {
      contextPrefix += `Pronoun: ${selectedLearner.pronoun}\n`;
    }
    selectedLearner.attributes
      .filter(attribute => attribute.value !== "")
      .forEach(attribute => {
        contextPrefix += `${attribute.key}: ${attribute.value}\n`;
      });
    selectedLearner.entityAvailabilitySchedule?.entityAvailabilities.forEach(
      availability => {
        const dayString = dayjs().day(availability.day).format("dddd");
        const startTimeString = dayjs()
          .hour(availability.startHour)
          .minute(availability.startMinute)
          .format("h:mm a");
        const endTimeString = dayjs()
          .hour(availability.endHour)
          .minute(availability.endMinute)
          .format("h:mm a");
        contextPrefix += `Availability: ${dayString} ${startTimeString} - ${endTimeString}\n`;
      }
    );

    contextPrefix += "--- Parent Message --- \n";
  }
  return contextPrefix + userMessage;
}

export const ConciergeInput = () => {
  const { showErrorMessage, sendConciergeRequestSubmit, selectedLearner } =
    useChatContext();
  const [inputValue, setInputValue] = useState("");

  const { t } = useTranslation("client\\components\\chat\\conciergeChat");
  const trackEvent = useTrackEvent();

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(event.target.value);
  };

  const [createConciergeSupportIssue] = useMutation<
    CreateConciergeSupportIssueMutation,
    CreateConciergeSupportIssueMutationVariables
  >(createConciergeSupportIssueMutation);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    trackEvent("concierge_request_submit_button_touch");
    const userMessage = buildUserMessage(inputValue, selectedLearner);
    try {
      await createConciergeSupportIssue({ variables: { userMessage } });
      sendConciergeRequestSubmit(inputValue, selectedLearner);
    } catch (error) {
      OsPlatform.captureError(error);
      showErrorMessage(
        inputValue,
        t`Sorry! We are not able to send your request at this time.`
      );
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <ChatInputField
        inputHelperText={t(
          'Example: "My 6th grader needs help with math homework in the evenings after school, especially with geometry"'
        )}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        handleSubmit={handleSubmit}
      >
        <LearnerSelection label={t("Select Learner (Optional)")} />
      </ChatInputField>
    </form>
  );
};
